import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import type { FieldDef, FieldStateEntry } from '../../orbit/lib/dossier-types';
import { EditableField, sectionProgress, subKey, type CommitFn } from './fieldKit';

/**
 * One dossier section, drawn as a card: a heading a person would recognise, how
 * much of it we already have, and every field in the section editable in place.
 *
 * Deliberately registry-driven. The old My Business blocks listed a hand-picked
 * handful of rows with labels typed into the JSX ("Name", "HQ") while the
 * progress count was computed over EVERY field in the section — so the badge
 * read 3/11 above three rows, and adding a field to the registry moved the
 * count without ever showing the field. Here the rows and the count come from
 * the same list, so they cannot disagree, and no label is written twice.
 */

export interface SectionCardProps {
  /** Section letter, e.g. 'a'. */
  letter: string;
  /** Human title for the section — never the letter on its own. */
  title: string;
  /** A plain-language line under the title saying what the section is for. */
  blurb?: string;
  /** Every field in this section, in registry order. */
  fields: FieldDef[];
  /** section_<letter> values keyed by sub-key. */
  values: Record<string, unknown>;
  /** field_states keyed by full dotted key. */
  fieldStates: Record<string, FieldStateEntry>;
  onCommit: (key: string, value: unknown, action: 'confirm' | 'correct') => void | Promise<unknown>;
  /** Save a beat after typing rather than on a Save button. */
  autosave?: boolean;
  /** Hide fields whose visibility rule does not currently pass. */
  hideIrrelevant?: boolean;
  /** Rendered above the field list — the stack card's tool icons, say. */
  children?: React.ReactNode;
  /** Rendered under the field list — the "add anything missing" control. */
  footer?: React.ReactNode;
  className?: string;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  letter, title, blurb, fields, values, fieldStates, onCommit,
  autosave = false, hideIrrelevant = true, children, footer, className,
}) => {
  const shown = useMemo(
    () => (hideIrrelevant ? fields.filter(f => !f.hidden) : fields),
    [fields, hideIrrelevant],
  );

  const stat = useMemo(() => sectionProgress(shown, values), [shown, values]);
  const done = stat.done;

  return (
    <section
      className={clsx('overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200/70', className)}
      aria-labelledby={`section-card-${letter}`}
    >
      <header className="px-5 pt-5">
        <div className="flex items-start gap-3">
          <div
            className={clsx(
              'grid h-8 w-8 shrink-0 place-items-center rounded-lg',
              done ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-600',
            )}
            aria-hidden="true"
          >
            {done
              ? <Check size={16} strokeWidth={2.25} />
              : <span className="font-sans text-[13px] font-semibold uppercase">{letter}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={`section-card-${letter}`} className="text-[15px] font-semibold tracking-tight text-gray-900">
              {title}
            </h2>
            {blurb && <p className="mt-0.5 text-[12.5px] leading-snug text-gray-500">{blurb}</p>}
          </div>
          <span className="shrink-0 font-sans text-[10.5px] uppercase tracking-wider tabular-nums text-gray-400">
            {stat.filled}/{stat.total}
          </span>
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
            style={{ width: `${stat.pct}%` }}
          />
        </div>
      </header>

      {children && <div className="px-5 pt-4">{children}</div>}

      <div className="mt-4 divide-y divide-gray-100 border-t border-gray-100">
        {shown.map(f => (
          <EditableField
            key={f.key}
            field={f}
            value={values[subKey(f.key)]}
            stateEntry={fieldStates[f.key]}
            autosave={autosave}
            onCommit={(v, action) => onCommit(f.key, v, action)}
          />
        ))}
        {shown.length === 0 && (
          <p className="px-5 py-6 text-center text-xs text-gray-400">
            Nothing to confirm in this section.
          </p>
        )}
      </div>

      {footer && <div className="border-t border-gray-100 px-5 py-4">{footer}</div>}
    </section>
  );
};

export default SectionCard;
